import { useRef, type KeyboardEvent, type MouseEvent } from "react";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { Ellipsis } from "lucide-react";
import { Link } from "react-router-dom";
import { TrilhoDoInterruptor } from "./Interruptor";

/*
 * O menu ••• da linha — coluna Ações da GUI da TecnoSpeed (design D4), sobre Radix
 * headless. A coluna que o usa declara `interativa` em `Tabela`.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ USAR O MENU NÃO ABRE A LINHA.                                            │
 * │                                                                          │
 * │ O conteúdo vai para um portal, mas o evento sintético do React sobe pela │
 * │ árvore de componentes, e chegaria ao `onClick` e ao `onKeyDown` da linha │
 * │ como se o portal não existisse. Por isso gatilho e conteúdo param os     │
 * │ dois aqui, e não em quem usa.                                            │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * `modal={false}`: o menu modal trava `pointer-events` no `body`, e o diálogo aberto
 * por um item herdaria a trava depois de fechar. A ação do item espera o menu fechar e
 * devolver o foco ao gatilho: só assim a `Confirmacao` que ela abre sabe para onde
 * devolver o foco ao terminar.
 */
export type ItemDoMenu =
  | {
      tipo: "acao";
      rotulo: string;
      aoEscolher: () => void;
      tom?: "destrutivo";
      desabilitado?: boolean;
    }
  | { tipo: "link"; rotulo: string; para: string }
  | {
      /** O interruptor de situação dentro do menu, com o estado à vista. */
      tipo: "interruptor";
      rotulo: string;
      ligado: boolean;
      aoAlternar: () => void;
    };

const ITEM =
  "flex h-8 cursor-pointer select-none items-center gap-3 rounded-[6px] px-2 text-corpo text-texto " +
  "outline-none data-[highlighted]:bg-fio data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";

function paraNaLinha(evento: MouseEvent | KeyboardEvent) {
  evento.stopPropagation();
}

export function MenuDeLinha({
  rotulo,
  itens,
}: {
  /** Nome acessível do gatilho, com a linha: "Ações de Clínica São Lucas". */
  rotulo: string;
  /** Sem itens, o gatilho não aparece: `leitura` não vê um menu vazio. */
  itens: readonly ItemDoMenu[];
}) {
  const acaoPendente = useRef<(() => void) | null>(null);

  if (itens.length === 0) return null;

  return (
    <Menu.Root modal={false}>
      <Menu.Trigger asChild>
        <button
          type="button"
          aria-label={rotulo}
          onClick={paraNaLinha}
          onKeyDown={paraNaLinha}
          className="inline-flex size-7 items-center justify-center rounded-controle text-texto-2 hover:bg-fio hover:text-texto data-[state=open]:bg-fio data-[state=open]:text-texto"
        >
          <Ellipsis aria-hidden="true" className="size-4" />
        </button>
      </Menu.Trigger>

      <Menu.Portal>
        {/*
          Acima do painel lateral (z-50), onde o menu também aparece, e abaixo da
          confirmação (z-70) que ele abre.
        */}
        <Menu.Content
          align="end"
          sideOffset={4}
          onClick={paraNaLinha}
          onKeyDown={paraNaLinha}
          onCloseAutoFocus={() => {
            const acao = acaoPendente.current;
            acaoPendente.current = null;
            acao?.();
          }}
          className="z-[55] min-w-[176px] rounded-controle border border-fio-forte bg-superficie-2 p-1"
        >
          {itens.map((item) => {
            switch (item.tipo) {
              case "link":
                return (
                  <Menu.Item key={item.rotulo} asChild className={ITEM}>
                    <Link to={item.para}>{item.rotulo}</Link>
                  </Menu.Item>
                );

              case "interruptor":
                return (
                  <Menu.CheckboxItem
                    key={item.rotulo}
                    checked={item.ligado}
                    onSelect={() => {
                      acaoPendente.current = item.aoAlternar;
                    }}
                    className={`${ITEM} justify-between`}
                  >
                    {item.rotulo}
                    <TrilhoDoInterruptor ligado={item.ligado} />
                  </Menu.CheckboxItem>
                );

              case "acao":
                return (
                  <Menu.Item
                    key={item.rotulo}
                    {...(item.desabilitado === true ? { disabled: true } : {})}
                    onSelect={() => {
                      acaoPendente.current = item.aoEscolher;
                    }}
                    className={`${ITEM} ${item.tom === "destrutivo" ? "text-falha" : ""}`}
                  >
                    {item.rotulo}
                  </Menu.Item>
                );
            }
          })}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
